
/**
 * External monitoring service integration
 * Provides adapters for popular monitoring services like Sentry, LogRocket, etc.
 */

import { useEffect, useState } from 'react';
import { logger, logError } from './logger';

// Interface for monitoring service adapter
export interface MonitoringServiceAdapter {
  initialize: (config: Record<string, any>) => Promise<boolean> | boolean;
  captureError: (error: Error, context?: Record<string, any>) => void;
  captureMessage: (message: string, level: string, context?: Record<string, any>) => void;
  capturePerformance: (name: string, duration: number, context?: Record<string, any>) => void;
  setUser: (userData: Record<string, any>) => void;
  shutdown: () => Promise<void> | void;
}

// Base adapter that logs to console but doesn't send to external service
class BaseAdapter implements MonitoringServiceAdapter {
  protected isInitialized = false;
  protected serviceName: string;

  constructor(serviceName: string = 'default') {
    this.serviceName = serviceName;
  }

  initialize(config: Record<string, any>): boolean {
    logger.info(`${this.serviceName} monitoring initialized`, 'monitoring', { configKeys: Object.keys(config) });
    this.isInitialized = true;
    return true;
  }

  captureError(error: Error, context?: Record<string, any>): void {
    logError(`[${this.serviceName}] Error captured`, 'monitoring', {
      error: error.message,
      stack: error.stack,
      ...context
    });
  }

  captureMessage(message: string, level: string, context?: Record<string, any>): void {
    logger.info(`[${this.serviceName}] Message captured: ${message}`, 'monitoring', {
      level,
      ...context
    });
  }

  capturePerformance(name: string, duration: number, context?: Record<string, any>): void {
    logger.info(`[${this.serviceName}] Performance: ${name}`, 'monitoring', {
      durationMs: duration,
      ...context
    });
  }

  setUser(userData: Record<string, any>): void {
    logger.info(`[${this.serviceName}] User set`, 'monitoring', { userData });
  }

  async shutdown(): Promise<void> {
    logger.info(`[${this.serviceName}] Shutdown`, 'monitoring');
  }
}

// Sentry adapter (mock implementation - would use actual Sentry SDK in production)
export class SentryAdapter extends BaseAdapter {
  constructor() {
    super('Sentry');
  }

  initialize(config: Record<string, any>): boolean {
    // In production, would initialize the actual Sentry SDK here
    // Example: Sentry.init({ dsn: config.dsn, environment: config.environment })
    logger.info('Sentry monitoring initialized', 'monitoring', { 
      environment: config.environment || 'development'
    });
    this.isInitialized = true;
    return true;
  }

  captureError(error: Error, context?: Record<string, any>): void {
    // In production: Sentry.captureException(error, { extra: context })
    super.captureError(error, context);
  }

  setUser(userData: Record<string, any>): void {
    // In production: Sentry.setUser({ id: userData.id, email: userData.email })
    super.setUser(userData);
  }
}

// LogRocket adapter (mock implementation)
export class LogRocketAdapter extends BaseAdapter {
  constructor() {
    super('LogRocket');
  }

  initialize(config: Record<string, any>): boolean {
    // In production: LogRocket.init(config.appId, { network: { requestSanitizer: ... } })
    logger.info('LogRocket monitoring initialized', 'monitoring', { 
      appId: config.appId 
    });
    this.isInitialized = true;
    return true;
  }

  setUser(userData: Record<string, any>): void {
    // In production: LogRocket.identify(userData.id, { name: userData.name, email: userData.email })
    super.setUser(userData);
  }
}

// Main monitoring service that can use multiple adapters
class MonitoringService {
  private static instance: MonitoringService;
  private adapters: Map<string, MonitoringServiceAdapter> = new Map();
  private isInitialized = false;
  private globalErrorsRegistered = false;
  
  private constructor() {}
  
  public static getInstance(): MonitoringService {
    if (!MonitoringService.instance) {
      MonitoringService.instance = new MonitoringService();
    }
    return MonitoringService.instance;
  }
  
  public registerAdapter(name: string, adapter: MonitoringServiceAdapter): void {
    this.adapters.set(name, adapter);
    logger.info(`Registered monitoring adapter: ${name}`, 'monitoring');
  }
  
  public async initialize(configs: Record<string, Record<string, any>> = {}): Promise<void> {
    logger.info('Initializing monitoring service', 'monitoring', { 
      adapterCount: this.adapters.size,
      configuredAdapters: Object.keys(configs)
    });
    
    for (const [name, adapter] of this.adapters.entries()) {
      try {
        const config = configs[name] || {};
        await adapter.initialize(config);
        logger.info(`Initialized ${name} adapter`, 'monitoring');
      } catch (err) {
        logError(`Failed to initialize ${name} adapter`, 'monitoring', {
          error: err instanceof Error ? err.message : String(err)
        });
      }
    }
    
    this.isInitialized = true;
    logger.info('Monitoring service initialization complete', 'monitoring');
    
    // Register global error handlers if not already registered
    this.registerGlobalErrorHandlers();
  }
  
  public captureError(error: Error, context?: Record<string, any>): void {
    if (!this.isInitialized) {
      logger.warn('Monitoring service not initialized', 'monitoring');
      return;
    }
    
    for (const [name, adapter] of this.adapters.entries()) {
      try {
        adapter.captureError(error, context);
      } catch (err) {
        logError(`Error in ${name} adapter captureError`, 'monitoring', {
          originalError: error.message,
          adapterError: err instanceof Error ? err.message : String(err)
        });
      }
    }
  }
  
  public captureMessage(message: string, level: string = 'info', context?: Record<string, any>): void {
    if (!this.isInitialized) {
      logger.warn('Monitoring service not initialized', 'monitoring');
      return;
    }
    
    for (const [name, adapter] of this.adapters.entries()) {
      try {
        adapter.captureMessage(message, level, context);
      } catch (err) {
        logError(`Error in ${name} adapter captureMessage`, 'monitoring', {
          message,
          adapterError: err instanceof Error ? err.message : String(err)
        });
      }
    }
  }
  
  public capturePerformance(name: string, duration: number, context?: Record<string, any>): (finalContext?: Record<string, any>) => void {
    if (!this.isInitialized) {
      logger.warn('Monitoring service not initialized', 'monitoring');
      return () => {};
    }
    
    for (const [name, adapter] of this.adapters.entries()) {
      try {
        adapter.capturePerformance(name, duration, context);
      } catch (err) {
        logError(`Error in ${name} adapter capturePerformance`, 'monitoring', {
          performanceName: name,
          adapterError: err instanceof Error ? err.message : String(err)
        });
      }
    }
    
    // Return a function that can be called to update performance data
    return (finalContext?: Record<string, any>) => {
      if (finalContext) {
        this.capturePerformance(name, duration, finalContext);
      }
    };
  }
  
  public setUser(userData: Record<string, any>): void {
    if (!this.isInitialized) {
      logger.warn('Monitoring service not initialized', 'monitoring');
      return;
    }
    
    for (const [name, adapter] of this.adapters.entries()) {
      try {
        adapter.setUser(userData);
      } catch (err) {
        logError(`Error in ${name} adapter setUser`, 'monitoring', {
          userId: userData.id,
          adapterError: err instanceof Error ? err.message : String(err)
        });
      }
    }
  }
  
  public async shutdown(): Promise<void> {
    logger.info('Shutting down monitoring service', 'monitoring');
    
    for (const [name, adapter] of this.adapters.entries()) {
      try {
        await adapter.shutdown();
        logger.info(`Shut down ${name} adapter`, 'monitoring');
      } catch (err) {
        logError(`Error shutting down ${name} adapter`, 'monitoring', {
          error: err instanceof Error ? err.message : String(err)
        });
      }
    }
  }

  private registerGlobalErrorHandlers(): void {
    if (this.globalErrorsRegistered || typeof window === 'undefined') {
      return;
    }
    
    // Handle unhandled errors
    window.addEventListener('error', (event) => {
      const error = event.error || new Error(`Script error: ${event.message}`);
      this.captureError(error, {
        source: event.filename,
        line: event.lineno,
        column: event.colno,
        type: 'window.onerror'
      });
    });
    
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const error = event.reason instanceof Error 
        ? event.reason 
        : new Error(String(event.reason));
      
      this.captureError(error, {
        type: 'unhandledrejection'
      });
    });
    
    this.globalErrorsRegistered = true;
    logger.info('Global error handlers registered', 'monitoring');
  }
}

// Export the singleton instance
export const monitoringService = MonitoringService.getInstance();

// Factory function for creating standard adapters
export const createAdapter = (type: 'sentry' | 'logrocket' | 'base'): MonitoringServiceAdapter => {
  switch (type) {
    case 'sentry':
      return new SentryAdapter();
    case 'logrocket': 
      return new LogRocketAdapter();
    default:
      return new BaseAdapter(type);
  }
};

// Helper function to start monitoring with common presets
export const initializeMonitoring = (
  services: Array<'sentry' | 'logrocket' | 'base'> = ['base'],
  configs: Record<string, Record<string, any>> = {}
): Promise<void> => {
  // Register each requested adapter
  services.forEach(service => {
    monitoringService.registerAdapter(service, createAdapter(service));
  });
  
  // Initialize the service with provided configs
  return monitoringService.initialize(configs);
};

// Export wrapper functions for direct use without the full service
export const captureError = (error: Error, context?: Record<string, any>) => 
  monitoringService.captureError(error, context);

export const captureMessage = (message: string, level: string = 'info', context?: Record<string, any>) => 
  monitoringService.captureMessage(message, level, context);

// Fix for "Type 'void' has no call signatures" errors
// Return a function instead of void to make it callable
export const capturePerformance = (name: string, duration: number, context?: Record<string, any>) => {
  return monitoringService.capturePerformance(name, duration, context);
};

export const setMonitoringUser = (userData: Record<string, any>) => 
  monitoringService.setUser(userData);

// Add the missing useRenderMonitoring hook
export const useRenderMonitoring = (componentName: string) => {
  useEffect(() => {
    const startTime = performance.now();
    
    return () => {
      const duration = performance.now() - startTime;
      capturePerformance(`${componentName}_render`, duration, {
        component: componentName
      });
    };
  }, [componentName]);
};

// New function to monitor React errors
export const useErrorTracking = (componentName: string) => {
  const [hasError, setHasError] = useState(false);
  
  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    setHasError(true);
    
    captureError(error, {
      component: componentName,
      errorInfo: errorInfo.componentStack,
      timestamp: new Date().toISOString()
    });
    
    logError(`Error in component: ${componentName}`, 'react-error', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack
    });
  };
  
  return { hasError, handleError };
};
